import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { DialogTitle, DialogDescription } from '../ui/dialog';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from '../ui/command';
import {
  Search,
  Home,
  MapPin,
  CreditCard,
  HelpCircle,
  BookOpen,
  LogIn,
  UserPlus,
  LayoutDashboard,
  FileText,
  Route as RouteIcon,
  Settings,
  Mail,
} from 'lucide-react';

const STATES = [
  ['Texas', 'TX'], ['California', 'CA'], ['Florida', 'FL'], ['New York', 'NY'],
  ['Pennsylvania', 'PA'], ['Illinois', 'IL'], ['Ohio', 'OH'], ['Georgia', 'GA'],
  ['North Carolina', 'NC'], ['Michigan', 'MI'], ['New Jersey', 'NJ'], ['Virginia', 'VA'],
  ['Washington', 'WA'], ['Arizona', 'AZ'], ['Massachusetts', 'MA'], ['Tennessee', 'TN'],
  ['Colorado', 'CO'], ['Maryland', 'MD'], ['Missouri', 'MO'], ['Indiana', 'IN'],
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const go = (path) => {
    setOpen(false);
    setTimeout(() => navigate(path), 60);
  };

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <VisuallyHidden.Root>
          <DialogTitle>Search Servio</DialogTitle>
          <DialogDescription>
            Quickly search the directory, browse states, or jump to any page using the keyboard.
          </DialogDescription>
        </VisuallyHidden.Root>
        <CommandInput placeholder="Search the directory… (type a state, page, or action)" />
        <CommandList>
          <CommandEmpty>Nothing matched. Try a state name.</CommandEmpty>

          <CommandGroup heading="Quick Actions">
            <CommandItem onSelect={() => go('/search')} data-testid="cmdk-find-server">
              <Search className="text-teal-600" />
              <span>Find a process server</span>
              <CommandShortcut>↵</CommandShortcut>
            </CommandItem>
            <CommandItem onSelect={() => go('/register')} data-testid="cmdk-list-business">
              <UserPlus className="text-teal-600" />
              <span>List my business</span>
            </CommandItem>
            <CommandItem onSelect={() => go('/pricing')} data-testid="cmdk-pricing">
              <CreditCard className="text-teal-600" />
              <span>View pricing</span>
            </CommandItem>
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Browse States">
            {STATES.map(([name, code]) => (
              <CommandItem
                key={code}
                onSelect={() => go(`/search?place_name=${encodeURIComponent(name)}&place_type=state&state=${code}`)}
              >
                <MapPin className="text-slate-400" />
                <span>{name}</span>
                <CommandShortcut className="font-mono text-[10px] tracking-widest">{code}</CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Pages">
            <CommandItem onSelect={() => go('/')}><Home /><span>Home</span></CommandItem>
            <CommandItem onSelect={() => go('/search')}><Search /><span>Find Servers</span></CommandItem>
            <CommandItem onSelect={() => go('/pricing#features')}><BookOpen /><span>Features</span></CommandItem>
            <CommandItem onSelect={() => go('/pricing#faq')}><HelpCircle /><span>FAQs</span></CommandItem>
            <CommandItem onSelect={() => go('/resources')}><BookOpen /><span>Resources</span></CommandItem>
            <CommandItem onSelect={() => go('/contact')}><Mail /><span>Contact</span></CommandItem>
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Account">
            <CommandItem onSelect={() => go('/login')}><LogIn /><span>Log in</span></CommandItem>
            <CommandItem onSelect={() => go('/dashboard')}><LayoutDashboard /><span>Dashboard</span></CommandItem>
            <CommandItem onSelect={() => go('/dashboard?tab=routes')}><RouteIcon /><span>Routes</span></CommandItem>
            <CommandItem onSelect={() => go('/dashboard?tab=invoices')}><FileText /><span>Invoices</span></CommandItem>
            <CommandItem onSelect={() => go('/dashboard?tab=settings')}><Settings /><span>Settings</span></CommandItem>
          </CommandGroup>
        </CommandList>
        <div className="border-t border-slate-100 px-4 py-2.5 flex items-center justify-between text-[11px] text-slate-400 bg-slate-50/60">
          <div className="flex items-center gap-3">
            <span><kbd className="font-mono">↑↓</kbd> navigate</span>
            <span><kbd className="font-mono">↵</kbd> select</span>
            <span><kbd className="font-mono">esc</kbd> close</span>
          </div>
          <span className="font-mono">Servio · ⌘K</span>
        </div>
      </CommandDialog>
    </>
  );
}

export default CommandPalette;
